import React, { useContext } from "react";
import { CloseCircleFilled, HomeOutlined, TagOutlined, TeamOutlined } from "@ant-design/icons";
import { Avatar, Popconfirm, Tooltip } from "antd";
import AssignmentMenu from "@Components/groups/AssignmentMenu";
import { GroupContext, SystemContext } from "@Constants";
import { TagType } from "@Components/groups/TagProperties";
import GroupsListTag from "@Components/groups/GroupsListTag";
import MiniSpinner from "@Components/shared/MiniSpinner";
import { IGroupEntry, ITag } from "@Components/groups/GroupUtils";
import { IAddressDetailsPaged } from "@Models/IAddressDetails";
import { ICreditDetailsPaged } from "@Models/ICreditDetails";
import { MainContext } from "@Context/provider";
import { findLowestContext, getSystemId, hasCapability } from "@Utils";
import { IAddAction, IDeleteAction } from "./types";

interface IProps {
    group: IGroupEntry;
    groupTags: ITag[];
    fetching: boolean;
    addAction: IAddAction;
    deleteAction: IDeleteAction;
    addressAssignments: IAddressDetailsPaged[];
    creditAssignments: ICreditDetailsPaged[];
}

const GroupsHeaderItem: React.FC<IProps> = ({
    group,
    fetching,
    groupTags,
    addAction,
    deleteAction,
    addressAssignments,
    creditAssignments,
}: IProps) => {
    const { setCurrentGroup, setCurrentUser } = useContext(MainContext);

    /**
     * Pop confirm remove group selected.
     */
    const removeGroup = () => {
        deleteAction(group);
    };

    /**
     * Allow group deletion, including base groups where lowest capability context allows.
     */
    const allowDelete = () => {
        return (
            group?.capacity < 0 &&
            hasCapability("UserGroup/Delete", GroupContext, group.groupId) &&
            (group.parentGroupId !== getSystemId() || findLowestContext("UserGroup/Delete") === SystemContext)
        );
    };

    const selectGroup = (e, selected) => {
        e.stopPropagation();

        // Update the ui state with the selected group. Null the current user.
        setCurrentUser(null);
        setCurrentGroup({ instance: selected });
    };

    const tags: JSX.Element[] = [];
    const addressTags = groupTags.filter((x) => x.groupId === group.groupId && x.type === TagType.AddressGroup);
    const creditTags = groupTags.filter((x) => x.groupId === group.groupId && x.type === TagType.CreditGroup);
    const licenseTags = groupTags.filter((x) => x.groupId === group.groupId && x.type === TagType.LicenseGroup);
    const smallTags = addressTags.length + creditTags.length + licenseTags.length > 5;

    if (addressTags?.length > 0) {
        tags.push(
            <GroupsListTag key={"g-" + group + "-g-" + group.groupId + "-address-t-0"} index={0} tag={addressTags[0]} />
        );
    }

    if (creditTags?.length > 0) {
        tags.push(
            <GroupsListTag key={"g-" + group + "-g-" + group.groupId + "-credit-t-0"} index={0} tag={creditTags[0]} />
        );
    }

    if (licenseTags?.length > 0) {
        tags.push(
            <GroupsListTag key={"g-" + group + "-g-" + group.groupId + "-license-t-0"} index={0} tag={licenseTags[0]} />
        );
    }

    if (fetching) {
        tags.push(
            <span
                key={"group-" + group.groupId + "-spin"}
                style={{
                    display: "inline-block",
                    justifyContent: "left",
                    paddingLeft: 10,
                }}
            >
                <MiniSpinner />
            </span>
        );
    }

    const canAddToGroups = hasCapability("UserGroup/AddMembers", GroupContext, group.groupId) && group?.capacity < 0;

    return (
        <div id="groups-list-header-content" onClick={(e) => selectGroup(e, group.groupId)}>
            <table>
                <tbody>
                    <tr>
                        <td id="groups-list-header-icon">
                            <Avatar
                                size="small"
                                className={"group-icon"}
                                style={{ marginRight: 10 }}
                                icon={
                                    group.level == 0 ? (
                                        <HomeOutlined />
                                    ) : group.capacity == -1 ? (
                                        <TeamOutlined />
                                    ) : (
                                        <TagOutlined />
                                    )
                                }
                            />
                        </td>
                        <td id="groups-list-header-row">
                            <span id="groups-list-header-title">{group.name}</span>
                        </td>
                        <td className={smallTags ? "small-tags" : ""}>{tags}</td>
                        <td>
                            {canAddToGroups ? (
                                <AssignmentMenu
                                    roleAssignments={[]}
                                    creditAssignments={creditAssignments}
                                    context={GroupContext}
                                    groupId={group.groupId}
                                    page={0}
                                    userId={""}
                                    addAction={addAction}
                                    systemRolesOnly={false}
                                />
                            ) : null}
                        </td>
                        <td id="groups-list-header-close">
                            {allowDelete() && (
                                <Tooltip title="Delete group">
                                    <Popconfirm
                                        title={"Delete this group and all child groups? This cannot be undone."}
                                        onConfirm={removeGroup}
                                        okText="Yes"
                                        cancelText="No"
                                        cancelButtonProps={{ className: "secondary" }}
                                    >
                                        <button className={"no-style groups-list-close-icon"}>
                                            <CloseCircleFilled />
                                        </button>
                                    </Popconfirm>
                                </Tooltip>
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
export default GroupsHeaderItem;
